/* eslint-disable ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  buildPolicyAcceptances,
  hasAcceptedPolicy,
  latestPolicy,
} from '@linc-technologies/linced-common/utils/policies';

// PolicyAcceptance centralises the logic for prompting a logged in person to
// accept updated policies. Consuming controllers load the policies via
// `loadPolicies()` (typically from init) and render the acceptance dialog using
// `hasAcceptedLatestPolicy`, `policyChanges` and `acceptTerms`.
export default class PolicyAcceptanceService extends Service {
  @service
  session;

  @service
  store;

  @service
  toaster;

  // policies holds the set of policies a person may be required to accept. See
  // utils/policies.
  @tracked
  policies = null;

  @computed(
    'policies.[]',
    'session.{person.policyAcceptances.[],school.jurisdiction}',
  )
  get hasAcceptedLatestPolicy() {
    // Dont display if nobody is logged in
    if (isEmpty(this.session.person)) {
      return true;
    }
    let policy = latestPolicy(this.policies);
    return !policy || hasAcceptedPolicy(this.session.person, policy);
  }

  // policyChanges lists the changes for the latest policy.
  @computed('policies.[]')
  get policyChanges() {
    let policy = latestPolicy(this.policies);
    return (policy && policy.changes) || [];
  }

  // loadPolicies fetches the policies a caregiver may need to accept.
  // Policy acceptance currently only applies to the NZ jurisdiction, so we
  // always fetch NZ policies regardless of which school is in the current
  // session context - a caregiver's session.school may be non-NZ even though
  // one of their other schools is NZ.
  loadPolicies() {
    this.store
      .query('policy', {
        jurisdiction: Jurisdiction.NZ_MOE,
      })
      .then((policies) => {
        // Resolved to a plain array: latestPolicy() reduces over it, which
        // throws on ember-data's still-pending query-result PromiseArray.
        this.policies = policies.slice();
      });
  }

  // acceptTerms records the latest policy (the one shown in the dialog) as
  // accepted and persists the person. Returns the save promise.
  acceptTerms() {
    let policy = latestPolicy(this.policies);
    this.session.person.set(
      'policyAcceptances',
      buildPolicyAcceptances(this.session.person, policy),
    );
    return this.session.person
      .save()
      .then(() => {
        this.toaster.success('Thanks for accepting our policy changes.');
      })
      .catch(() => {
        this.toaster.error('Error accepting policy changes.');
      });
  }
}
